import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu } from '@components/ui/Menu/Menu';
import type { GroupId, ISchedulerGroup, SiteId } from '@interfaces/IScheduler';
import { useSchedulerActions, useSchedulerPanelOpen } from '../../hooks/useSchedulerStore';
import styles from './TaskPanel.module.css';
import { isImeComposing } from '@utils/ime';

interface IGroupSelectProps {
  id?: string;
  siteId: SiteId;
  groups: ISchedulerGroup[];
  value: GroupId;
  onChange: (groupId: GroupId) => void;
  /** When true, the popup opens automatically on first mount and focuses the create-new input. */
  openOnMount?: boolean;
}

export function GroupSelect({ id, siteId, groups, value, onChange, openOnMount = false }: IGroupSelectProps) {
  const { t } = useTranslation();
  const { addGroup } = useSchedulerActions();
  const panelOpen = useSchedulerPanelOpen();
  const afterGroupId = panelOpen?.afterGroupId;

  const [open, setOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Fire openOnMount exactly once per mount. Base UI's Menu unmounts the
  // popup when closed, so input focusing has to happen after we flip `open`.
  const armedRef = useRef(openOnMount);
  useEffect(() => {
    if (armedRef.current) {
      setOpen(true);
      armedRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    const handle = setTimeout(() => inputRef.current?.focus(), 30);
    return () => clearTimeout(handle);
  }, [open]);

  const currentGroup = groups.find((group) => group.id === value);

  const submitCreate = useCallback(() => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    const newId = `g-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    addGroup({
      siteId,
      group: { id: newId, name: trimmed, collapsed: false },
      afterGroupId,
    });
    onChange(newId);
    setNewName('');
    setOpen(false);
  }, [newName, addGroup, siteId, afterGroupId, onChange]);

  return (
    <Menu.Root open={open} onOpenChange={setOpen}>
      <Menu.Trigger
        render={
          <button id={id} type="button" className={styles.groupSelectTrigger}>
            <span className={styles.groupSelectValue}>{currentGroup?.name ?? '—'}</span>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
        }
      />
      <Menu.Portal>
        <Menu.Positioner side="bottom" align="start" sideOffset={4}>
          <Menu.Popup className={styles.groupSelectPopup}>
            {groups.map((group) => (
              <Menu.Item
                key={group.id}
                onClick={() => {
                  onChange(group.id);
                  setOpen(false);
                }}
              >
                {group.name}
              </Menu.Item>
            ))}
            <Menu.Separator />
            <div
              className={styles.groupSelectCreateRow}
              // Stop the menu's keyboard nav from interfering with the text input.
              onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => event.stopPropagation()}
            >
              <input
                ref={inputRef}
                type="text"
                value={newName}
                onChange={(event) => setNewName(event.target.value)}
                placeholder={t('scheduler.rowActions.newGroupPlaceholder')}
                className={styles.groupSelectCreateInput}
                onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
                  if (event.key === 'Enter' && !isImeComposing(event)) {
                    event.preventDefault();
                    submitCreate();
                  }
                }}
              />
              <button
                type="button"
                onClick={submitCreate}
                disabled={!newName.trim()}
                className={styles.groupSelectCreateBtn}
                aria-label={t('scheduler.rowActions.createNewGroup')}
                title={t('scheduler.rowActions.createNewGroup')}
              >
                +
              </button>
            </div>
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  );
}
